import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadialBarChart, RadialBar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, AppWindow, Clock, FileText, RefreshCw, ExternalLink, Zap, TrendingUp, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import { useAuth } from '../contexts/AuthContext';
import { StatusBadge, StatusDot } from '../components/StatusBadge';
import SystemHealthSummary from '../components/SystemHealthSummary';
import SuperAdminLaunchpad from '../components/SuperAdminLaunchpad';
import LaunchpadErrorBoundary from '../components/LaunchpadErrorBoundary';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const SEVERITY_COLORS = { success: '#10B981', info: '#3B82F6', warning: '#F59E0B', error: '#EF4444' };
const SEVERITY_ICONS = { success: '✓', info: 'i', warning: '!', error: '✗' };

function StatCard({ label, value, icon: Icon, color = 'primary', sub }) {
  return (
    <div className="glass rounded-xl p-5 border border-white/5 hover:border-primary/20 transition-colors group" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${color === 'success' ? 'bg-status-up/10' : color === 'warning' ? 'bg-status-degraded/10' : color === 'error' ? 'bg-status-down/10' : 'bg-primary/10'}`}>
          <Icon className={`w-4 h-4 ${color === 'success' ? 'text-status-up' : color === 'warning' ? 'text-status-degraded' : color === 'error' ? 'text-status-down' : 'text-primary'}`} />
        </div>
        <TrendingUp className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="text-2xl font-heading font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-xs text-primary mt-0.5 font-mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [checking, setChecking] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [appsRes, actRes, tasksRes, postsRes] = await Promise.all([
        api.get('/apps/'),
        api.get('/activity/?limit=10'),
        api.get('/tasks/'),
        api.get('/posts/'),
      ]);
      setApps(asArray(appsRes.data));
      setActivity(asArray(actRes.data));
      setTasks(asArray(tasksRes.data));
      setPosts(asArray(postsRes.data));
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const runHealthCheck = async (appId, appName) => {
    setChecking(appId);
    try {
      await api.post(`/apps/${appId}/health-check`);
      toast.success(`Health check complete for ${appName}`);
      await fetchAll();
    } catch {
      toast.error('Health check failed');
    } finally {
      setChecking(null);
    }
  };

  const statusCounts = apps.reduce((acc, app) => {
    const s = app.latest_health?.status || 'unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const pieData = [
    { name: 'UP', value: statusCounts.up || 0, color: '#10B981' },
    { name: 'DEGRADED', value: statusCounts.degraded || 0, color: '#F59E0B' },
    { name: 'DOWN', value: statusCounts.down || 0, color: '#EF4444' },
    { name: 'UNKNOWN', value: statusCounts.unknown || 0, color: '#64748B' },
  ].filter(d => d.value > 0);

  const draftPosts = posts.filter(p => p.status === 'draft').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {user?.is_owner && (
        <LaunchpadErrorBoundary>
          <SuperAdminLaunchpad />
        </LaunchpadErrorBoundary>
      )}

      {(user?.is_owner || user?.is_super_admin) && <SystemHealthSummary />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Operations Dashboard</h1>
          <p className="text-muted-foreground text-sm font-mono mt-0.5">
            {new Date().toLocaleString('en-AT', { dateStyle: 'full', timeStyle: 'short' })}
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 hover:border-primary/30 text-muted-foreground hover:text-foreground transition-colors text-sm"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Apps" value={apps.length} icon={AppWindow} />
        <StatCard label="Apps Online" value={statusCounts.up || 0} icon={Zap} color="success" sub={`${apps.length > 0 ? Math.round(((statusCounts.up || 0) / apps.length) * 100) : 0}% last reported online`} />
        <StatCard label="Active Tasks" value={tasks.filter(t => t.enabled).length} icon={Clock} color="warning" />
        <StatCard label="Draft Posts" value={draftPosts} icon={FileText} />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* App Health Grid */}
        <div className="lg:col-span-2 glass rounded-xl border border-white/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-foreground">App Health Status</h2>
            <button
              onClick={() => navigate('/apps')}
              className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
              data-testid="view-all-apps-btn"
            >
              View all <ExternalLink className="w-3 h-3" />
            </button>
          </div>

          {apps.length === 0 ? (
            <div className="text-center py-12">
              <AppWindow className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No apps registered yet</p>
              <button
                onClick={() => navigate('/apps')}
                className="mt-3 text-primary text-sm hover:text-primary/80 transition-colors"
              >
                Add your first app
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {apps.map(app => (
                <div
                  key={app.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/apps/${app.id}`)}
                  data-testid={`app-health-row-${app.id}`}
                >
                  <StatusDot status={app.latest_health?.status || 'unknown'} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground">{app.name}</span>
                    <span className="text-xs text-muted-foreground ml-2 font-mono">{app.environment}</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {app.latest_health?.response_time_ms != null ? `${app.latest_health.response_time_ms}ms` : '—'}
                  </div>
                  <StatusBadge status={app.latest_health?.status || 'unknown'} />
                  <button
                    onClick={(e) => { e.stopPropagation(); runHealthCheck(app.id, app.name); }}
                    disabled={checking === app.id}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-primary"
                    title="Run health check"
                    data-testid={`check-btn-${app.id}`}
                  >
                    <RefreshCw className={`w-3 h-3 ${checking === app.id ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Health Chart + Summary */}
        <div className="space-y-4">
          {apps.length > 0 && (
            <div className="glass rounded-xl border border-white/5 p-5">
              <h2 className="font-heading font-bold text-foreground mb-4">Health Overview</h2>
              <div className="flex justify-center">
                <PieChart width={160} height={160}>
                  <Pie data={pieData.length > 0 ? pieData : [{ name: 'UNKNOWN', value: 1, color: '#64748B' }]} cx={75} cy={75} innerRadius={45} outerRadius={70} dataKey="value" strokeWidth={0}>
                    {(pieData.length > 0 ? pieData : [{ color: '#64748B' }]).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#090E16', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(val, name) => [`${val} app${val !== 1 ? 's' : ''}`, name]}
                  />
                </PieChart>
              </div>
              <div className="space-y-1.5 mt-2">
                {[{ label: 'UP', color: '#10B981', count: statusCounts.up || 0 }, { label: 'DEGRADED', color: '#F59E0B', count: statusCounts.degraded || 0 }, { label: 'DOWN', color: '#EF4444', count: statusCounts.down || 0 }, { label: 'UNKNOWN', color: '#64748B', count: statusCounts.unknown || 0 }].filter(s => s.count > 0).map(s => (
                  <div key={s.label} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    <span className="text-muted-foreground font-mono">{s.label}</span>
                    <span className="ml-auto text-foreground font-semibold">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="glass rounded-xl border border-white/5 p-5">
            <h2 className="font-heading font-bold text-foreground mb-3">Quick Actions</h2>
            <div className="space-y-2">
              {[
                { label: 'Add App', icon: AppWindow, action: () => navigate('/apps?add=true') },
                { label: 'New Task', icon: Clock, action: () => navigate('/automations?add=true') },
                { label: 'New Post', icon: FileText, action: () => navigate('/posts?add=true') },
                { label: 'View Activity', icon: Activity, action: () => navigate('/activity') },
              ].map(({ label, icon: Icon, action }) => (
                <button
                  key={label}
                  onClick={action}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors text-left"
                  data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-3.5 h-3.5 text-primary" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="glass rounded-xl border border-white/5 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-foreground">Recent Activity</h2>
          <button onClick={() => navigate('/activity')} className="text-xs text-primary hover:text-primary/80 transition-colors">
            View all
          </button>
        </div>
        {activity.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-6">No activity yet. Run a health check to get started.</p>
        ) : (
          <div className="space-y-3">
            {activity.map(event => (
              <div key={event.id} className="flex items-start gap-3" data-testid={`activity-item-${event.id}`}>
                <div
                  className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                  style={{ background: `${SEVERITY_COLORS[event.severity]}20`, color: SEVERITY_COLORS[event.severity] }}
                >
                  {SEVERITY_ICONS[event.severity]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground leading-tight">{event.message}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {event.app_name && <span className="text-xs text-muted-foreground font-mono">{event.app_name}</span>}
                    <span className="text-xs text-muted-foreground font-mono">
                      {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
